import type { BookingArticle, Customer } from "@common/types";
import { BookingStatus } from "@common/types";

/** A single, short-lived draft in this browser tab, isolated by account and role. */
export interface BookingDraft {
  version: 1;
  savedAt: number;
  date: string;
  selectedTrip: string;
  departureLocationId: string;
  selectedPartnerId: string;
  selectedUserId: string;
  selectedTimeSlot: string;
  useCustomTime: boolean;
  customStartTime: string;
  customDurationHours: string;
  numberOfGuests: number;
  includeLunch: boolean;
  customer: Customer;
  notes: string;
  customTripPrice: string;
  extraCost: string;
  extraCostDescription: string;
  bookingReference: string;
  articles: BookingArticle[];
  initialStatus: BookingStatus.PENDING | BookingStatus.CONFIRMED;
}

export function bookingDraftKey(user: { id: string; role: string } | null): string | null {
  return user ? `seaslot-booking-draft:${user.id}:${user.role}` : null;
}

const strings = ["date", "selectedTrip", "departureLocationId", "selectedPartnerId", "selectedUserId", "selectedTimeSlot", "customStartTime", "customDurationHours", "notes", "customTripPrice", "extraCost", "extraCostDescription", "bookingReference"] as const;
function isDraft(value: unknown): value is BookingDraft {
  if (!value || typeof value !== "object") return false;
  const d = value as Record<string, unknown>;
  if (d.version !== 1 || typeof d.savedAt !== "number" || !Number.isFinite(d.savedAt)) return false;
  if (!strings.every(key => typeof d[key] === "string")) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.date as string)) return false;
  const date = new Date(`${d.date}T12:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== d.date) return false;
  if (typeof d.numberOfGuests !== "number" || !Number.isFinite(d.numberOfGuests)) return false;
  if (typeof d.useCustomTime !== "boolean" || typeof d.includeLunch !== "boolean") return false;
  if (d.initialStatus !== BookingStatus.PENDING && d.initialStatus !== BookingStatus.CONFIRMED) return false;
  if (!d.customer || typeof d.customer !== "object") return false;
  const customer = d.customer as Record<string, unknown>;
  if (!["name", "country", "mobile"].every(key => typeof customer[key] === "string") || (customer.email !== undefined && typeof customer.email !== "string")) return false;
  return Array.isArray(d.articles) && d.articles.every(article => article && typeof article === "object" &&
    typeof article.articleNumber === "string" && typeof article.description === "string" &&
    typeof article.quantity === "number" && Number.isFinite(article.quantity) &&
    typeof article.unitPrice === "number" && Number.isFinite(article.unitPrice));
}

export function readBookingDraft(key: string | null): BookingDraft | null {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (isDraft(value) && value.savedAt <= Date.now() && Date.now() - value.savedAt < 8 * 60 * 60 * 1000) return value;
    sessionStorage.removeItem(key);
  } catch { /* Storage can be unavailable; booking still works. */ }
  return null;
}

export function saveBookingDraft(key: string | null, draft: BookingDraft): boolean {
  if (!key) return false;
  try { sessionStorage.setItem(key, JSON.stringify(draft)); return true; }
  catch { return false; }
}

export function clearBookingDraft(key: string | null): void {
  if (!key) return;
  try { sessionStorage.removeItem(key); } catch { /* No stored draft to recover. */ }
}
